import React, { useState, useEffect, useRef, useCallback } from 'react';
import { FiSearch, FiUsers, FiX } from 'react-icons/fi';
import { PiCheckBold, PiPlusBold } from 'react-icons/pi';
import {
  useCollaboratorSearch,
  hasCollaboratorFilters,
  type UserSearchResult,
} from '../../api/media';
import { CollaboratorFilterBar, useDebouncedValue } from './CollaboratorFilterBar';
import { useAuth } from '../../hooks/useAuth';
import { Avatar } from '../ui/Avatar';

interface CollaboratorsSelectorProps {
  selectedIds: number[];
  onToggle: (id: number) => void;
  /** Full user objects for already-selected collaborators (used in edit mode) */
  initialSelected?: UserSearchResult[];
  /**
   * Whether to render the section heading inside the component.
   * Set to false when the parent already renders a section heading
   * (e.g. EventForm renders its own `<h2>Collaborators</h2>`).
   * Defaults to true so that standalone usages still show a heading.
   */
  showHeading?: boolean;
  /** Event forms only allow searching by name; hides the user type and city filters. */
  searchOnly?: boolean;
}

export const CollaboratorsSelector: React.FC<CollaboratorsSelectorProps> = ({
  selectedIds,
  onToggle,
  initialSelected = [],
  showHeading = true,
  searchOnly = false,
}) => {
  const [search, setSearch] = useState('');
  const [userType, setUserType] = useState('');
  const [city, setCity] = useState('');
  const sentinelRef = useRef<HTMLDivElement>(null);

  const { user: currentUser } = useAuth();

  // Cache of full user objects we've seen (initialSelected + search results)
  // so we can always render selected chips even when search is empty.
  const [knownUsers, setKnownUsers] = useState<UserSearchResult[]>(initialSelected);

  // Debounce: wait 400ms after the user stops typing before firing the query
  const debouncedSearch = useDebouncedValue(search);
  const debouncedCity = useDebouncedValue(city);
  const filters = searchOnly
    ? { search: debouncedSearch }
    : { search: debouncedSearch, userType, city: debouncedCity };
  const filtersActive = hasCollaboratorFilters(filters);

  const { data, isLoading, isFetchingNextPage, fetchNextPage, hasNextPage } =
    useCollaboratorSearch(filters);

  // Flatten all pages into a single list and exclude current user.
  // Without an active filter nothing is fetched, so nothing is shown.
  const results = filtersActive
    ? (data?.pages.flatMap((p) => p.results) ?? []).filter(
        (u) => String(u.id) !== String(currentUser?.id),
      )
    : [];

  // Merge newly fetched users into the known-users cache
  useEffect(() => {
    if (results.length === 0) return;
    setKnownUsers((prev) => {
      const existingIds = new Set(prev.map((u) => u.id));
      const newOnes = results.filter((u) => !existingIds.has(u.id));
      return newOnes.length ? [...prev, ...newOnes] : prev;
    });
  }, [results]);

  // Trigger next page fetch when the sentinel div scrolls into view
  const handleObserver = useCallback(
    (entries: IntersectionObserverEntry[]) => {
      if (entries[0].isIntersecting && hasNextPage && !isFetchingNextPage) {
        fetchNextPage();
      }
    },
    [fetchNextPage, hasNextPage, isFetchingNextPage],
  );

  useEffect(() => {
    const el = sentinelRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(handleObserver, { threshold: 0.1 });
    observer.observe(el);
    return () => observer.disconnect();
  }, [handleObserver]);

  const displayName = (u: UserSearchResult) => u.full_name?.trim() || u.username?.trim() || 'User';

  // Derive the full objects for currently selected IDs
  const selectedUsers = knownUsers.filter((u) => selectedIds.includes(u.id));

  return (
    <div className="flex flex-col gap-6">
      {showHeading && <h3 className="text-white text-lg font-semibold">Collaborators</h3>}

      {/* Selected chips */}
      {selectedUsers.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {selectedUsers.map((u) => (
            <button
              key={u.id}
              type="button"
              onClick={() => onToggle(u.id)}
              className="flex items-center gap-2 bg-blue-600/10 border border-blue-500/40 rounded-full pl-2 pr-3 py-1.5 hover:bg-red-500/10 hover:border-red-500/40 transition-all group max-w-35"
            >
              <Avatar
                src={u.prof_pics_url}
                alt=""
                name={u.full_name?.trim() || u.username?.trim() || 'U'}
                size="xs"
                className="shrink-0"
              />
              <span className="text-blue-400 group-hover:text-red-400 text-[13px] font-medium transition-colors truncate min-w-0 overflow-hidden">
                {u.full_name?.trim() || u.username?.trim() || 'User'}
              </span>
              <FiX size={12} className="text-blue-400 group-hover:text-red-400 transition-colors" />
            </button>
          ))}
        </div>
      )}

      {/* Search input */}
      <div className="relative flex items-center bg-zinc-900/40 border border-white/5 rounded-2xl px-4 h-14 focus-within:border-blue-500/50 transition-all">
        <FiSearch size={20} className="text-blue-500 shrink-0" />
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search collaborators"
          className="ml-3 bg-transparent border-none text-white text-[15px] font-normal placeholder:text-zinc-600 focus:ring-0 focus:outline-none w-full"
        />
        {search && (
          <button
            type="button"
            onClick={() => setSearch('')}
            className="text-zinc-500 hover:text-zinc-300 shrink-0 transition-colors"
          >
            <FiX size={18} />
          </button>
        )}
      </div>

      {!searchOnly && (
        <CollaboratorFilterBar
          userType={userType}
          city={city}
          onUserTypeChange={setUserType}
          onCityChange={setCity}
        />
      )}

      {/* Content — bounded scrollable area */}
      <div className="overflow-y-auto max-h-96 pr-1">
        {isLoading ? (
          /* Skeleton grid — matches the real user grid layout */
          <div className="grid grid-cols-4 sm:grid-cols-5 gap-y-8 gap-x-4">
            {Array.from({ length: 10 }).map((_, i) => (
              <div key={i} className="flex flex-col items-center gap-2">
                <div className="w-14 h-14 rounded-full bg-white/5 animate-pulse" />
                <div className="h-2.5 w-10 rounded bg-white/5 animate-pulse" />
              </div>
            ))}
          </div>
        ) : results.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-10 text-center gap-3">
            <div className="w-16 h-16 rounded-full bg-white/5 flex items-center justify-center text-zinc-600">
              {filtersActive ? <FiSearch size={28} /> : <FiUsers size={28} />}
            </div>
            <p className="text-zinc-500 text-sm">
              {filtersActive
                ? 'No users match your search'
                : searchOnly
                  ? 'Search by name or username to find collaborators'
                  : 'Search by name, or filter by user type or city, to find collaborators'}
            </p>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-4 sm:grid-cols-5 gap-y-8 gap-x-4">
              {results.map((u) => {
                const isSelected = selectedIds.includes(u.id);
                // Use prof_pics_url from the API response
                const avatar = u.prof_pics_url;
                return (
                  <button
                    key={u.id}
                    type="button"
                    onClick={() => onToggle(u.id)}
                    className="flex flex-col items-center gap-2 group"
                  >
                    <div className="relative">
                      <div
                        className={`w-14 h-14 rounded-full border-2 overflow-hidden transition-all duration-300 flex items-center justify-center bg-zinc-800 ${
                          isSelected
                            ? 'border-blue-500 ring-4 ring-blue-500/20'
                            : 'border-white/10 group-hover:border-white/30'
                        }`}
                      >
                        <Avatar
                          src={avatar}
                          alt={displayName(u)}
                          name={displayName(u)}
                          size="xl"
                          className="border-2 border-black"
                        />
                      </div>
                      <div
                        className={`absolute -bottom-1 -right-1 w-6 h-6 rounded-full flex items-center justify-center border-2 border-black transition-all duration-300 ${
                          isSelected ? 'bg-green-500 text-white' : 'bg-blue-600 text-white'
                        }`}
                      >
                        {isSelected ? <PiCheckBold size={14} /> : <PiPlusBold size={14} />}
                      </div>
                    </div>
                    <span
                      className={`text-[12px] font-medium text-center truncate w-full transition-colors ${
                        isSelected ? 'text-white' : 'text-zinc-500 group-hover:text-zinc-300'
                      }`}
                    >
                      {displayName(u)}
                    </span>
                  </button>
                );
              })}
            </div>

            {/* Infinite scroll sentinel */}
            <div ref={sentinelRef} className="flex justify-center py-3 min-h-6">
              {isFetchingNextPage && (
                <div className="w-6 h-6 rounded-full border-2 border-blue-500 border-t-transparent animate-spin" />
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
};
